import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllVariants } from '../../services/api/subscriptionService';
import styles from './VariantSelect.module.css';
import Markdown from 'markdown-to-jsx';
import Button, { ButtonType } from '../general-components/Button';
import { Circle } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';

const Variant = ({ v, isEnterprise }: { v: any; isEnterprise: boolean }) => {
    const { t } = useTranslation();

    const getFeaturesList = (isEnterprise: boolean) => {
        const features = isEnterprise
            ? (t('signup.variantSelect.features.enterprise.list', {
                  returnObjects: true,
              }) as string[])
            : (t('signup.variantSelect.features.individual.list', {
                  returnObjects: true,
              }) as string[]);

        return features.map((feature: string) => `- ${feature}`).join('\n');
    };

    return (
        <div className={styles.info}>
            <div className={styles.price}>
                {!isEnterprise && (
                    <>
                        <h1>
                            $
                            {(v.price / 100) % 1 > 0
                                ? (v.price / 100).toFixed(2)
                                : (v.price / 100).toFixed(0)}
                        </h1>{' '}
                        {t('signup.variantSelect.pricing.per')} {v.interval}
                    </>
                )}
                {isEnterprise && (
                    <h1>{t('signup.variantSelect.pricing.letsTalk')}</h1>
                )}
            </div>
            {v.interval === 'year' && (
                <span className={styles.discount}>
                    {t('signup.variantSelect.save.yearlyDiscount')}
                </span>
            )}
            <Markdown className={styles.description}>
                {`### ${t(
                    'signup.variantSelect.features.individual.title'
                )}\n${getFeaturesList(isEnterprise)}`}
            </Markdown>
        </div>
    );
};

const VariantSelect = ({
    onSelect,
}: {
    onSelect: (variantId: string) => void;
}) => {
    const { t } = useTranslation();
    const [products, setProducts] = useState([]);
    const [selectedVariant, setSelectedVariant] = useState();
    const [hasEnterprise, setHasEnterprise] = useState(false);
    const [isTrial, setIsTrial] = useState(false);
    const [searchParams] = useSearchParams();

    const updateVariant = (v: any) => {
        setSelectedVariant(v);
        // onSelect(v);
    };

    const fetchVariants = async () => {
        const p = await getAllVariants();

        const trial = searchParams.get('trial');
        setIsTrial(trial === 'true');
        let filteredProducts = p;
        if (trial === 'false') {
            filteredProducts = p.filter((v: any) => !v.isTrial);
        } else {
            filteredProducts = p.filter((v: any) => v.isTrial);
        }
        setProducts(filteredProducts.reverse());
        updateVariant(
            filteredProducts.filter((v: any) => v.name === 'Monthly')[0]?.id
        );
    };

    useEffect(() => {
        const enterprise = searchParams.get('enterprise');
        setHasEnterprise(enterprise === 'true');
        fetchVariants();
    }, []);

    return (
        <>
            {products.length > 0 && (
                <>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <div className={styles.save}>
                            <span>
                                {t('signup.variantSelect.save.yearlyBilling')}
                            </span>
                        </div>
                        <div className={styles.toggleContainer}>
                            <div className={styles.toggle}>
                                {products.map((p) => (
                                    <span
                                        onClick={() => updateVariant(p.id)}
                                        className={`${styles.button} ${
                                            selectedVariant === p.id
                                                ? styles.selected
                                                : ''
                                        }`}
                                    >
                                        {p.name}
                                    </span>
                                ))}
                            </div>
                            {/* <span className={styles.badge}>Save 17%</span> */}
                        </div>
                    </div>
                    <div className={styles.variants}>
                        <div className={styles.container}>
                            <div className={styles.titleContainer}>
                                <label className={styles.title}>
                                    <Circle
                                        style={{
                                            height: '15px',
                                            width: '15px',
                                        }}
                                    />
                                    {t('signup.variantSelect.types.individual')}
                                </label>
                            </div>
                            {products
                                .filter((v) => v.id === selectedVariant)
                                .map((v) => (
                                    <>
                                        <div
                                            className={styles.trial}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {isTrial && (
                                                <div className={styles.save}>
                                                    <span>
                                                        {t(
                                                            'signup.variantSelect.trial.badge'
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                            <Variant
                                                v={v}
                                                isEnterprise={false}
                                            />
                                        </div>
                                        <div className={styles.select}>
                                            <Button
                                                small
                                                onClick={() => onSelect(v.id)}
                                                type={ButtonType.secondary}
                                                className={styles.subButton}
                                            >
                                                {' '}
                                                {t(
                                                    'signup.variantSelect.buttons.subscribeNow'
                                                )}
                                            </Button>
                                        </div>
                                    </>
                                ))}
                        </div>
                        {hasEnterprise && (
                            <div className={styles.container}>
                                <div className={styles.titleContainer}>
                                    <label className={styles.title}>
                                        <Circle
                                            style={{
                                                height: '15px',
                                                width: '15px',
                                            }}
                                        />
                                        {t(
                                            'signup.variantSelect.types.enterprise'
                                        )}
                                    </label>
                                </div>

                                <Variant v={{}} isEnterprise={true} />
                                <div className={styles.select}>
                                    <Button
                                        small
                                        onClick={() => {
                                            window.open(
                                                `${process.env.REACT_APP_CONTACT_FORM}`,
                                                '_blank'
                                            );
                                        }}
                                        type={ButtonType.secondary}
                                    >
                                        {' '}
                                        {t(
                                            'signup.variantSelect.buttons.contactUs'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
            {/* <div className={styles.container}>
                <h2>Department</h2>
                <div className={styles.info}>
                    <div className={styles.price}>
                        <h1>500 $/Scanner/year</h1>
                    </div>
                    <div className={styles.description}>
                        <Markdown>
                            ### Features - Search - Filter - View Conditionals
                        </Markdown>
                    </div>
                </div>
            </div> */}
        </>
    );
};

export default VariantSelect;
